import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { client } from "../../..";
import { WA_SEND_TEXT, WA_ADD_NOTE, WA_USE_QUICK_REPLY } from "../../../Queries/WhatsApp";
import { upsertWaConversation, upsertWaMessage } from "../../../Slices/whatsappSlice";
import { sendErrorText, formatBytes, gqlErrorMessage, isWindowOpen, uploadWhatsAppMedia, windowRemaining } from "./waUtils";

const EMOJIS = ["😀", "😊", "🙂", "😉", "👍", "🙏", "👏", "🎉", "✅", "📄", "📎", "📅", "⏰", "📞", "💼", "🤝", "❤️", "😅", "🤔", "👋"];
const ACCEPT = "image/jpeg,image/png,video/mp4,video/3gpp,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt";
const drafts = new Map(); // conversationId → unsent text (survives switching threads)

const Composer = ({ conversation, canAct, ownerName, quickReplies, maxTextLength, maxCaptionLength, onOpenTemplates, onOpenQuickReplies, insertRequest, now }) => {
  const dispatch = useDispatch();
  const userName = useSelector((s) => s.whatsapp.status?.userName);
  const [mode, setMode] = useState("reply"); // reply | note
  const [text, setText] = useState(() => drafts.get(conversation.id) || "");
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [qrIndex, setQrIndex] = useState(0);
  const textRef = useRef(null);
  const fileRef = useRef(null);

  const windowOpen = isWindowOpen(conversation, now);
  const remaining = windowRemaining(conversation.windowExpiresAt, now);
  const replyBlocked = mode === "reply" && (!windowOpen || !canAct);

  // Swap drafts when the conversation changes.
  useEffect(() => {
    setText(drafts.get(conversation.id) || "");
    setFile(null);
    setError(null);
    setShowEmoji(false);
    setMode("reply");
  }, [conversation.id]);

  useEffect(() => {
    if (text) drafts.set(conversation.id, text);
    else drafts.delete(conversation.id);
  }, [text, conversation.id]);

  useEffect(() => {
    if (!file || !file.type.startsWith("image/")) {
      setFilePreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setFilePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Quick reply chosen from the side panel → insert it.
  useEffect(() => {
    if (!insertRequest?.reply) return;
    setMode("reply");
    applyQuickReply(insertRequest.reply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insertRequest?.nonce]);

  // Auto-grow the textarea.
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(160, el.scrollHeight)}px`;
  }, [text]);

  // "/shortcut" quick-reply suggestions (reply mode only).
  const qrQuery = mode === "reply" && /^\/[\w-]*$/.test(text) ? text.slice(1).toLowerCase() : null;
  const qrMatches = useMemo(() => {
    if (qrQuery === null) return [];
    return (quickReplies || [])
      .filter((q) => !qrQuery || q.shortcut?.includes(qrQuery) || q.title.toLowerCase().includes(qrQuery))
      .slice(0, 8);
  }, [qrQuery, quickReplies]);
  useEffect(() => setQrIndex(0), [qrQuery]);

  const applyQuickReply = (q) => {
    const firstName = String(conversation.contactName || "").split(/\s+/)[0] || "";
    const body = q.body
      .replace(/\{first_name\}/gi, firstName)
      .replace(/\{name\}/gi, conversation.contactName || "")
      .replace(/\{my_name\}/gi, userName || "");
    setText(body);
    client.mutate({ mutation: WA_USE_QUICK_REPLY, variables: { id: q.id } }).catch(() => {});
    setTimeout(() => textRef.current?.focus(), 0);
  };

  const limit = file ? maxCaptionLength : maxTextLength;
  const tooLong = text.length > limit;

  const send = async () => {
    if (sending) return;
    const body = text.trim();
    setError(null);

    if (mode === "note") {
      if (!body) return;
      setSending(true);
      try {
        const { data } = await client.mutate({ mutation: WA_ADD_NOTE, variables: { conversationId: conversation.id, body } });
        dispatch(upsertWaMessage(data.whatsappAddNote));
        setText("");
      } catch (err) {
        setError({ message: gqlErrorMessage(err) });
      } finally {
        setSending(false);
      }
      return;
    }

    if (replyBlocked || tooLong || (!body && !file)) return;
    setSending(true);
    try {
      let result;
      if (file) {
        setProgress(0);
        result = await uploadWhatsAppMedia({ conversationId: conversation.id, file, caption: body, onProgress: setProgress });
        if (!result.ok) {
          setError({ code: result.code, message: result.message });
          return;
        }
      } else {
        const { data } = await client.mutate({ mutation: WA_SEND_TEXT, variables: { conversationId: conversation.id, body } });
        result = data.whatsappSendText;
        if (!result.ok) {
          setError({ code: result.code, message: result.error });
          return;
        }
      }
      if (result.message) dispatch(upsertWaMessage(result.message));
      if (result.conversation) dispatch(upsertWaConversation(result.conversation));
      setText("");
      setFile(null);
      textRef.current?.focus();
    } catch (err) {
      setError({ message: gqlErrorMessage(err) });
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (qrMatches.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setQrIndex((i) => Math.min(qrMatches.length - 1, i + 1)); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setQrIndex((i) => Math.max(0, i - 1)); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); applyQuickReply(qrMatches[qrIndex]); return; }
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setText(""); return; }
    }
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey || !e.shiftKey)) {
      e.preventDefault();
      send();
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setError(null);
    setFile(f);
    textRef.current?.focus();
  };

  if (!canAct && mode === "reply") {
    return (
      <div className="wa-composer">
        <div className="wa-composer__closed">
          <span><i className="fa-solid fa-lock" /> Assigned to <b>{ownerName || "a teammate"}</b>. Only they or a WhatsApp admin can reply.</span>
          <button type="button" className="wa-btn wa-btn--sm" onClick={() => setMode("note")}><i className="fa-solid fa-note-sticky" /> Add note</button>
        </div>
      </div>
    );
  }

  return (
    <div className="wa-composer">
      <div className="wa-composer__mode" role="tablist" aria-label="Composer mode">
        <button type="button" role="tab" aria-selected={mode === "reply"} className={mode === "reply" ? "is-active" : ""} onClick={() => setMode("reply")} disabled={!canAct}>
          <i className="fa-solid fa-reply" /> Reply
        </button>
        <button type="button" role="tab" aria-selected={mode === "note"} className={`is-note ${mode === "note" ? "is-active" : ""}`} onClick={() => setMode("note")}>
          <i className="fa-solid fa-note-sticky" /> Internal note
        </button>
        <span style={{ flex: 1 }} />
        {mode === "reply" && (
          <button type="button" onClick={onOpenTemplates} disabled={!canAct} title="Send an approved template">
            <i className="fa-solid fa-file-lines" /> Template
          </button>
        )}
      </div>

      {error && (
        <div className="wa-alert wa-alert--danger" style={{ marginBottom: 8 }} role="alert">
          <i className="fa-solid fa-circle-exclamation" style={{ marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            {sendErrorText(error)}
          </div>
          {(error.code === "WINDOW_CLOSED" || error.code === "NO_OPT_IN") && (
            <button type="button" className="wa-btn wa-btn--sm" onClick={onOpenTemplates}>Use template</button>
          )}
          <button type="button" className="wa-icon-btn" onClick={() => setError(null)} aria-label="Dismiss"><i className="fa-solid fa-xmark" /></button>
        </div>
      )}

      {mode === "reply" && !windowOpen ? (
        <div className="wa-composer__closed">
          <span>
            <i className="fa-regular fa-clock" />{" "}
            {conversation.lastInboundAt
              ? "The 24-hour reply window is closed. Send an approved template — free replies reopen when they respond."
              : "This contact hasn't messaged yet. Start with an approved template."}
          </span>
          <button type="button" className="wa-btn wa-btn--primary wa-btn--sm" onClick={onOpenTemplates}>
            <i className="fa-solid fa-file-lines" /> Choose template
          </button>
        </div>
      ) : (
        <>
          {qrMatches.length > 0 && (
            <div className="wa-qr-menu" role="listbox" aria-label="Quick replies">
              {qrMatches.map((q, i) => (
                <button type="button" key={q.id} role="option" aria-selected={i === qrIndex} className={`wa-qr-item ${i === qrIndex ? "is-active" : ""}`} onMouseDown={(e) => { e.preventDefault(); applyQuickReply(q); }}>
                  <b>{q.title}</b>{q.shortcut && <code>/{q.shortcut}</code>}
                  <div>{q.body}</div>
                </button>
              ))}
            </div>
          )}
          {showEmoji && mode === "reply" && (
            <div className="wa-emoji-row">
              {EMOJIS.map((em) => (
                <button type="button" key={em} onClick={() => { setText((t) => t + em); textRef.current?.focus(); }} aria-label={`Insert ${em}`}>{em}</button>
              ))}
            </div>
          )}
          {file && (
            <div className="wa-composer__attach">
              {filePreview ? <img src={filePreview} alt="" /> : <i className="fa-regular fa-file-lines" style={{ fontSize: 26, color: "var(--wa-accent)" }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</div>
                <div className="wa-help" style={{ margin: 0 }}>{formatBytes(file.size)} · {sending ? `Uploading ${progress}%` : "Your text below becomes the caption"}</div>
              </div>
              <button type="button" className="wa-icon-btn" onClick={() => setFile(null)} disabled={sending} aria-label="Remove attachment"><i className="fa-solid fa-xmark" /></button>
            </div>
          )}
          <div className={`wa-composer__box ${mode === "note" ? "wa-composer__box--note" : ""}`}>
            {mode === "reply" && (
              <>
                <button type="button" className="wa-icon-btn" onClick={() => fileRef.current?.click()} disabled={sending} title="Attach a file" aria-label="Attach a file">
                  <i className="fa-solid fa-paperclip" />
                </button>
                <button type="button" className="wa-icon-btn" onClick={() => setShowEmoji((v) => !v)} title="Emoji" aria-label="Emoji" aria-pressed={showEmoji}>
                  <i className="fa-regular fa-face-smile" />
                </button>
                <input ref={fileRef} type="file" accept={ACCEPT} hidden onChange={onPickFile} />
              </>
            )}
            <textarea
              ref={textRef}
              rows={1}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={mode === "note" ? "Note for your team — the contact never sees this" : file ? "Add a caption (optional)" : "Type a message — / for quick replies"}
              aria-label={mode === "note" ? "Internal note" : "Message"}
              disabled={sending}
            />
            {mode === "reply" && (
              <button type="button" className="wa-icon-btn" onClick={onOpenQuickReplies} title="Quick replies" aria-label="Quick replies">
                <i className="fa-solid fa-bolt" />
              </button>
            )}
            <button
              type="button"
              className="wa-btn wa-btn--primary"
              onClick={send}
              disabled={sending || tooLong || (mode === "reply" ? replyBlocked || (!text.trim() && !file) : !text.trim())}
              aria-label={mode === "note" ? "Save note" : "Send"}
            >
              {sending ? <i className="fa-solid fa-spinner wa-spin" /> : <i className={mode === "note" ? "fa-solid fa-check" : "fa-solid fa-paper-plane"} />}
            </button>
          </div>
          <div className="wa-composer__foot">
            <span>
              {mode === "reply" && remaining ? <><span className="wa-dot wa-dot--open" /> Free replies for {remaining}</> : mode === "note" ? "Visible to your team only" : ""}
            </span>
            <span style={{ color: tooLong ? "var(--wa-danger)" : undefined }}>
              {text.length > limit * 0.8 ? `${text.length}/${limit} · ` : ""}
              <span className="wa-kbd">Enter</span> send · <span className="wa-kbd">Shift</span>+<span className="wa-kbd">Enter</span> new line
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default Composer;
